/*
 * Image
 *
 * Supports:
 * - Retina
 * - Loading thumbnail
 * - Lazy loading of full resolution src
 * - Webp src for suitable browsers
 */

import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import {
    useLazyImage,
    useWebp
} from '$hooks/images';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const Image = ({
    alt,
    className,
    containerClassName,
    containerStyle,
    height,
    loading: loadingSrc,
    loadingStyle,
    psrc,
    px2,
    role,
    src,
    style,
    width,
    x2
}) => {
    const imageRef = useRef();
    const webp = useWebp();

    const imageSrc = webp ? psrc : src;
    const imageX2 = webp ? px2 : x2;

    const {
        loading,
        loaded
    } = useLazyImage({
        imageRef,
        src: imageSrc
    });

    return (
        <div
            className={containerClassName}
            style={{
                ...{
                    height: 0,
                    overflow: 'hidden',
                    paddingBottom: width && height ? `${(height / width) * 100}%` : 'initial',
                    position: 'relative',
                    width: '100%'
                },
                ...containerStyle
            }}>
            <div style={{
                bottom: 0,
                left: 0,
                position: 'absolute',
                right: 0,
                top: 0
            }}>
                {loadingSrc && (
                    <div
                        className='pixelated'
                        style={{
                            ...{
                                backgroundImage: `url(${loadingSrc})`,
                                opacity: loaded ? 0 : 1,
                                height: '100%',
                                position: 'absolute',
                                transition: 'opacity 2s',
                                width: '100%',
                            },
                            ...loadingStyle
                        }} />
                )}

                <img
                    alt={alt}
                    className={className}
                    height={height}
                    ref={imageRef}
                    role={role}
                    src={(loading || loaded) ? imageSrc : null}
                    srcSet={(loading || loaded) && imageX2 ?`${imageX2} 2x` : null}
                    style={{
                        ...{
                            opacity: loaded ? 1 : 0,
                            position: 'absolute',
                            top: 0,
                            transition: 'opacity .3s',
                            width: '100%'
                        },
                        ...style
                    }}
                    width={width} />
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    alt: PropTypes.string,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
    containerStyle: PropTypes.object,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    loading: PropTypes.string,
    loadingStyle: PropTypes.object,
    psrc: PropTypes.string,
    px2: PropTypes.string,
    role: PropTypes.string,
    src: PropTypes.string.isRequired,
    style: PropTypes.object,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    x2: PropTypes.string
};

Image.propTypes = propTypes;

export default Image;
